import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'wouter'

import { cn } from '@/lib/cn'
import { SITE_SECTIONS, siteSectionFor } from '@/lib/nav'

import styles from './siteTabs.module.css'

export interface SiteTabsProps {
  /** The public site has its own gold palette; the app surfaces use tokens. */
  variant?: 'console' | 'public'
  className?: string
}

/**
 * The three top-level destinations: Wanxiang, extraction, and the console.
 *
 * These are links, not an ARIA tablist: switching moves the router and often
 * changes the surrounding shell, so a tablist's roving-focus contract would
 * describe a widget that is not there. `aria-current` gives assistive technology
 * the same active state the underline shows.
 */
export function SiteTabs({ variant = 'console', className }: SiteTabsProps) {
  const { t } = useTranslation('console')
  const [location] = useLocation()
  const active = siteSectionFor(location)

  return (
    <nav
      className={cn(styles.tabs, variant === 'public' && styles.public, className)}
      aria-label={t('site.label')}
    >
      {SITE_SECTIONS.map((section) => (
        <Link
          key={section.id}
          href={section.href}
          className={styles.tab}
          aria-current={section.id === active ? 'page' : undefined}
        >
          {t(section.labelKey)}
        </Link>
      ))}
    </nav>
  )
}
